/**
 * Lo que comparten las SEIS pantallas que completan un tratamiento (Planner, Tareas,
 * panel de próximas, ficha de equipo, ficha de trabajador y el hook de acciones):
 * los códigos de «falta stock» que devuelve complete-treatment-task y el texto del
 * aviso de «registrar igualmente».
 *
 Hasta el 01-09-2026 aquí vivía «Registrar igualmente»: volver a llamar con
 * `auto_topup: true` y que el servidor se inventara lo que faltaba. Se quitó
 * (13 usos en una semana en Frutas Nijasol, 789,51 € de stock que nunca existió).
 * Lo que queda en su sitio: leer el desglose de lo que falta y poder pedirlo.
 */

/**
 * ¿Completar esta tarea tiene que MOVER INVENTARIO?
 *
 * La pregunta correcta es «¿lleva productos?», y hay tres formas de llevarlos:
 * una receta guardada (`treatment_id`), productos elegidos a mano
 * (`custom_products` con `product_id`), o el campo antiguo de un solo producto
 * (`product_id`). Si la respuesta es sí, completar tiene que pasar por
 * `complete-treatment-task`: es la ÚNICA función que escribe el parte de
 * tratamiento (`spray_logs`), sus líneas y el descuento de stock — las tres cosas
 * en la misma secuencia, y el descuento va colgado del id de la línea.
 *
 * Por qué existe esta función y no una condición suelta en cada pantalla: la
 * tarjeta «Próximas tareas» del panel de inicio se escribió en noviembre de 2025
 * preguntando solo por `treatment_id`, cuando una receta era la única manera de
 * poner productos en una tarea. Al añadir los productos a mano, las otras
 * pantallas aprendieron la pregunta nueva y esa no. Un tratamiento con productos
 * elegidos a mano completado desde el panel de inicio se marcaba en verde sin
 * parte y sin descontar nada, sin ningún error: la función que hace el trabajo no
 * llegaba a llamarse. Pasó en esamapa el 18-08-2026 a las 06:25 (4 L de AGRIMEC,
 * 375 ml de KARATE ZEON y 7,5 L de ULTRA-PROM), y quedan dos tareas así en Frutas
 * Nijasol. Con la regla en un solo sitio, la próxima pantalla que complete tareas
 * la hereda en vez de reinventarla.
 */
export function taskNeedsInventoryRun(task: {
  type?: string | null;
  treatment_id?: string | null;
  product_id?: string | null;
  custom_products?: unknown;
}): boolean {
  if (task?.type !== "treatment") return false;
  const hasCustom = Array.isArray(task.custom_products)
    && (task.custom_products as Array<{ product_id?: string | null }>).some((cp) => !!cp?.product_id);
  return !!(task.treatment_id || hasCustom || task.product_id);
}

export const STOCK_BLOCK_CODES = [
  "INSUFFICIENT_STOCK",
  "LOCATION_STOCK_NOT_FOUND",
  "LOCATION_STOCK_INSUFFICIENT",
  "INVENTORY_DEDUCTION_FAILED",
] as const;

export type StockBlockCode = (typeof STOCK_BLOCK_CODES)[number];

export function isStockBlockCode(code: unknown): code is StockBlockCode {
  return typeof code === "string" && (STOCK_BLOCK_CODES as readonly string[]).includes(code);
}

/** Una línea del desglose de «falta stock» que devuelve complete-treatment-task. */
export interface MissingProduct {
  product_id: string;
  product_name: string;
  unit: string;
  /** Lo que pide el tratamiento. */
  required: number;
  /** Lo que hay en la finca de la tarea. */
  available_here: number;
  location_id: string | null;
  location_name: string | null;
  /** Dónde SÍ hay: lo que convierte el aviso en una instrucción. */
  elsewhere: Array<{ location_id: string; location_name: string; quantity: number }>;
}

/* La forma mínima del `t` de i18next que se usa aquí (clave + opciones). Se declara
   así, y no como TFunction, para que cualquier `t` de useTranslation encaje sin cast. */
type TFn = (key: string, options?: Record<string, unknown>) => unknown;

/**
 * El desglose de lo que falta, venga como venga.
 *
 * `complete-treatment-task` devuelve `missing[]` desde el 01-09-2026, pero la
 * versión desplegada puede ir por detrás del código (publicar NO redespliega las
 * funciones), así que si no viene se reconstruye lo que se pueda de los campos
 * viejos —`products`, `product_name`, `required`, `available`—. Más vale un
 * aviso con menos detalle que una pantalla en blanco.
 */
export function parseMissingProducts(body: unknown): MissingProduct[] {
  const b = (body ?? {}) as Record<string, unknown>;

  if (Array.isArray(b.missing) && b.missing.length > 0) {
    return (b.missing as MissingProduct[]).filter((m) => m && m.product_name);
  }

  const locationName = typeof b.location_name === "string" ? b.location_name : null;
  const fallbackElsewhere = Array.isArray(b.available_locations)
    ? (b.available_locations as string[])
        .filter((n) => typeof n === "string" && n)
        .map((n) => ({ location_id: "", location_name: n, quantity: 0 }))
    : [];

  const names = Array.isArray(b.products) && b.products.length > 0
    ? (b.products as string[])
    : typeof b.product_name === "string" && b.product_name
      ? [b.product_name]
      : [];

  return names.map((name) => ({
    product_id: "",
    product_name: name,
    unit: typeof b.unit === "string" ? b.unit : "",
    required: Number(b.required) || 0,
    available_here: Number(b.available) || 0,
    location_id: null,
    location_name: locationName,
    elsewhere: fallbackElsewhere,
  }));
}

/** «COSTAR (500g) · hacen falta 1 kg, hay 0» — una línea por producto. */
export function describeMissing(m: MissingProduct): string {
  const n = (v: number) => v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return `${m.product_name} · ${n(m.required)} ${m.unit}`.trim();
}

/**
 * El texto de un «no se ha podido completar» que NO es falta de stock, en el
 * idioma de la persona (el servidor responde en inglés). Auditoría del perito,
 * 26-09-2026:
 *  - PERMISSION_DENIED: su rol no completa tratamientos (D2: el perito receta y
 *    planifica, no aplica). Antes se leía «Stock insuficiente» (T05-01).
 *  - AMOUNT_MISSING / AREA_MISSING: la tarea no dice cuánto, o la dosis es por
 *    hectárea y el sector no tiene superficie. Antes el servidor se inventaba
 *    1 kg o 1 ha (A14 · T05-03).
 * Para cualquier otro código devuelve el mensaje que venga (o null).
 */
export function completionErrorText(body: unknown, t: TFn): string | null {
  const b = (body ?? {}) as Record<string, unknown>;
  const product = typeof b.product_name === "string" ? b.product_name : "";
  switch (b.error) {
    case "PERMISSION_DENIED":
      return String(t("planner.completeTreatmentNoPermission"));
    case "AMOUNT_MISSING":
      return String(t("planner.completeAmountMissing", { product }));
    case "AREA_MISSING":
      return String(t("planner.completeAreaMissing", { product }));
    default:
      return typeof b.message === "string" && b.message ? b.message : null;
  }
}
